import { PlusIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import AddToTableDialog from "./AddToTableDialog";
import { useRequestLogsSelectionStore } from "./request-logs-selection-context";

import { buildStructuredSearchPayload } from "@/routes/request-logs/search/structured-search-payload";
import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import {
  GridSelectionLimitControl,
  GridSelectionSelectAllMatching,
  HistoryImportActionButton,
  submitHistoryImport,
  useGridSelectionActionBarLimit,
  type HistoryImportConfig,
} from "@/components/ui/ag-grid-table";
import { Button } from "@/components/ui/button";
import EntityIcon from "@/components/ui/icons/EntityIcon";
import { ToastType } from "@/enums";
import { EntityType } from "@/queries/folder-entities/types";
import { mapStructuredFilterGroupToBackend } from "@/queries/request-log-query";
import type { SmartTableRequestImportPayload } from "@/queries/smart-tables";
import { displayToast } from "@/utils/toast";

export type RequestImportConfig = HistoryImportConfig<SmartTableRequestImportPayload>;

export interface SignalSampleConfig {
  maxSampleSize: number;
  currentSampleCount: number;
  submitSample: (requestLogIds: number[]) => Promise<boolean> | boolean;
  handleSuccess: () => void;
}

enum SelectionAddActionMode {
  SignalSample = "signal_sample",
  RequestImport = "request_import",
  AddToTable = "add_to_table",
}

interface SelectionActionBarProps {
  totalMatching: number | null;
  onAddToTableEnqueueSuccess?: () => void;
  requestImportConfig?: RequestImportConfig;
  signalSampleConfig?: SignalSampleConfig;
}

interface SelectionAddActionProps {
  mode: SelectionAddActionMode;
  selectedCount: number;
  requestImportConfig?: RequestImportConfig;
  isTableDialogOpen: boolean;
  setIsTableDialogOpen: (open: boolean) => void;
  onRequestImport: () => void;
  onSignalSample: () => void;
  buildPayload: () => SmartTableRequestImportPayload | null;
  onAddToTableEnqueueSuccess?: () => void;
  onClearSelection: () => void;
}

const SelectionAddAction = ({
  mode,
  selectedCount,
  requestImportConfig,
  isTableDialogOpen,
  setIsTableDialogOpen,
  onRequestImport,
  onSignalSample,
  buildPayload,
  onAddToTableEnqueueSuccess,
  onClearSelection,
}: SelectionAddActionProps) => {
  switch (mode) {
    case SelectionAddActionMode.SignalSample:
      return (
        <Button size="sm" onClick={() => void onSignalSample()}>
          <PlusIcon className="mr-1.5 h-4 w-4" />
          Add to Sample
        </Button>
      );
    case SelectionAddActionMode.RequestImport:
      if (!requestImportConfig) {
        return null;
      }
      return (
        <HistoryImportActionButton
          config={requestImportConfig}
          selectedCount={selectedCount}
          onClick={onRequestImport}
        />
      );
    case SelectionAddActionMode.AddToTable:
      return (
        <>
          <Button size="sm" onClick={() => setIsTableDialogOpen(true)}>
            <EntityIcon type={EntityType.SMART_TABLE} size="xs" className="mr-1.5 text-white" />
            Add to Table
          </Button>
          <AddToTableDialog
            isOpen={isTableDialogOpen}
            setOpen={setIsTableDialogOpen}
            buildPayload={buildPayload}
            onEnqueueSuccess={() => {
              onClearSelection();
              onAddToTableEnqueueSuccess?.();
            }}
          />
        </>
      );
    default:
      return null;
  }
};

const getSelectionAddActionMode = ({
  signalSampleConfig,
  requestImportConfig,
}: {
  signalSampleConfig?: SignalSampleConfig;
  requestImportConfig?: RequestImportConfig;
}): SelectionAddActionMode => {
  if (signalSampleConfig) {
    return SelectionAddActionMode.SignalSample;
  }
  if (requestImportConfig) {
    return SelectionAddActionMode.RequestImport;
  }
  return SelectionAddActionMode.AddToTable;
};

const SelectionActionBar = observer(
  ({
    totalMatching,
    onAddToTableEnqueueSuccess,
    requestImportConfig,
    signalSampleConfig,
  }: SelectionActionBarProps) => {
    const selection = useRequestLogsSelectionStore();
    const structuredSearchStore = useStructuredSearchStore();
    const filterStore = structuredSearchStore;
    const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
    const selectedCount = selection.count;
    const hasSelection = selectedCount > 0;
    const {
      selectAllLimitInput,
      isAllMatching,
      handleLimitInputChange,
      handleSelectAllMatching,
      handleLimitInputBlur,
    } = useGridSelectionActionBarLimit({ selection, totalMatching });

    const showSelectAllMatching =
      !signalSampleConfig &&
      requestImportConfig?.selectionLimit !== 1 &&
      selection.mode === "selected" &&
      totalMatching !== null &&
      totalMatching > selection.selectedCount;

    const buildRequestImportPayload = (): SmartTableRequestImportPayload | null => {
      if (selection.mode === "all_matching") {
        const structuredPayload = buildStructuredSearchPayload(
          structuredSearchStore.filters,
          structuredSearchStore.dateRange,
        );
        const filterGroup = mapStructuredFilterGroupToBackend(structuredPayload.filterGroup);
        if (!filterGroup) {
          return null;
        }

        const sortState = filterStore.sorting[0];

        return {
          filter_group: filterGroup,
          ...(selection.matchingLimit ? { limit: selection.matchingLimit } : {}),
          ...(filterStore.q ? { q: filterStore.q } : {}),
          ...(sortState?.id ? { sort_by: sortState.id } : {}),
          ...(sortState ? { sort_order: sortState.desc ? "desc" : "asc" } : {}),
        };
      }

      return {
        request_log_ids: selection.selectedIds,
      };
    };

    const handleRequestImport = () => {
      if (!requestImportConfig) {
        return;
      }
      void submitHistoryImport({
        config: requestImportConfig,
        selection,
        entityLabel: "request",
        buildPayload: buildRequestImportPayload,
      });
    };

    const handleSignalSample = async () => {
      if (!signalSampleConfig) {
        return;
      }

      if (selection.mode === "all_matching") {
        displayToast("Select individual requests to add to the signal sample.", ToastType.error);
        return;
      }

      const remainingSlots =
        signalSampleConfig.maxSampleSize - signalSampleConfig.currentSampleCount;
      if (remainingSlots <= 0) {
        displayToast(
          `The signal sample is full (${signalSampleConfig.maxSampleSize} requests). Remove requests before adding more.`,
          ToastType.error,
        );
        return;
      }

      if (selectedCount > remainingSlots) {
        displayToast(
          `You can add ${remainingSlots} more request${remainingSlots === 1 ? "" : "s"} to the sample.`,
          ToastType.error,
        );
        return;
      }

      const didAdd = await signalSampleConfig.submitSample(selection.selectedIds);
      if (!didAdd) {
        return;
      }

      selection.clear();
      signalSampleConfig.handleSuccess();
    };

    if (!hasSelection) {
      return null;
    }

    const summary = `${selectedCount.toLocaleString()} selected`;
    const addActionMode = getSelectionAddActionMode({ signalSampleConfig, requestImportConfig });
    const showStandaloneLimit =
      !signalSampleConfig && requestImportConfig?.selectionLimit !== 1 && isAllMatching;

    return (
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-900">{summary}</span>
        <Button variant="ghost" size="sm" onClick={() => selection.clear()}>
          Clear
        </Button>
        {showSelectAllMatching && totalMatching !== null && (
          <GridSelectionSelectAllMatching
            totalMatching={totalMatching}
            limitInput={selectAllLimitInput}
            onSelectAllMatching={handleSelectAllMatching}
            onLimitChange={handleLimitInputChange}
            onLimitBlur={handleLimitInputBlur}
          />
        )}
        {showStandaloneLimit && (
          <GridSelectionLimitControl
            totalMatching={totalMatching}
            value={selectAllLimitInput}
            showSelectAllMatching={false}
            onChange={handleLimitInputChange}
            onBlur={handleLimitInputBlur}
          />
        )}
        <SelectionAddAction
          mode={addActionMode}
          selectedCount={selectedCount}
          requestImportConfig={requestImportConfig}
          isTableDialogOpen={isTableDialogOpen}
          setIsTableDialogOpen={setIsTableDialogOpen}
          onRequestImport={handleRequestImport}
          onSignalSample={handleSignalSample}
          buildPayload={buildRequestImportPayload}
          onAddToTableEnqueueSuccess={onAddToTableEnqueueSuccess}
          onClearSelection={() => selection.clear()}
        />
      </div>
    );
  },
);

export default SelectionActionBar;
